import {
    buildOfferFormData,
    createFormEntry,
    deleteFormEntry,
    EDITOR_FORM_ENTRY,
    EDITOR_FORM_ENTRY_CUSTOMER_CHANGED,
    EDITOR_FORM_ENTRY_ENTITY_CHANGED,
    EDITOR_FORM_ENTRY_FORM_CHANGED,
    EDITOR_FORM_ENTRY_FORM_DATA_CHANGED,
    EDITOR_FORM_ENTRY_SET,
    entityByID,
    formEntryName,
    getFormEntry,
    getFormForNewFormEntry,
    updateFormEntryFrontend,
    validateRequiredFields,
} from "@rpforms/shared";
import * as actions from "@rpforms/shared/build/actions/formEntries";
import { Loader } from "@rpforms/shared/build/components/universal/Loader";
import PageNavAction from "@rpforms/shared/build/components/universal/PageNavAction";
import PageNavbar from "@rpforms/shared/build/components/universal/PageNavbar";
import {
    doNothing,
    getFormData,
    isVisible,
} from "@rpforms/shared/build/utils";
import { push } from "connected-react-router";
import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import { useLocation, withRouter } from "react-router-dom";
import { toast } from "react-toastify";
import FormEntryFilter from "../../../components/entries/FormEntryFilter";
import { FormRenderer } from "../../../components/FormRenderer";
import {
    IZugferdBtPayload,
    ModalBuyerOrderReference,
} from "../../../components/modal/ModalBuyerOrderReference";
import { RootState } from "../../../reducers";
import { getFormNames } from "redux-form";

const NewEntryPage = () => {
    const formEntry = useSelector<RootState, IFormEntry>((state) => state.formEntries.data);
    const isLoading = useSelector<RootState, IFormEntry>((state) => state.formEntries.isLoading);
    const entities = useSelector<RootState, any>((state) => state.entities.autocomplete);
    const customers = useSelector<RootState, any>((state) => state.customers.autocomplete);

    const [metaUnlocked, setMetaUnlocked] = useState(false);
    const [metaChanged, setMetaChanged] = useState(false);
    const [showBuyerOrderModal, setShowBuyerOrderModal] = useState(false);
    const [pendingSubmissionMode, setPendingSubmissionMode] = useState<string | null>(null);
    const [buyerOrderReference, setBuyerOrderReference] = useState("");
    const [zugferdBtFields, setZugferdBtFields] = useState<any>({});

    const [prefillParams, setPrefillParams] = useState<{
        customerId?: number | null;
        entityId?: number | null;
        formId?: number | null;
        mode?: string | null;
    } | null>(null);

    const [prefillFormApplied, setPrefillFormApplied] = useState(false);
    const [prefillCustomerApplied, setPrefillCustomerApplied] = useState(false);
    const [prefillEntityApplied, setPrefillEntityApplied] = useState(false);
    const [offerPrefillApplied, setOfferPrefillApplied] = useState(false);
    const [offerPrefillVersion, setOfferPrefillVersion] = useState(0);

    const dispatch = useDispatch();

    const { formEntryId } = useParams<{ formEntryId: string }>();
    const location = useLocation();

    useEffect(() => {
        // Anything in here is fired on component mount.
        return () => {
            dispatch({ type: EDITOR_FORM_ENTRY });
        };
    }, []);

    useEffect(() => {
        if (formEntryId != null) {
            dispatch(
                getFormEntry(parseInt(formEntryId), (formEntryData) => {
                    dispatch({
                        type: EDITOR_FORM_ENTRY_SET,
                        payload: formEntryData,
                    });
                })
            );
        } else {
            dispatch({
                type: EDITOR_FORM_ENTRY,
            });
        }
    }, [formEntryId]);

    useEffect(() => {
        if (formEntryId != null) {
            return;
        }

        const params = new URLSearchParams(location.search);
        const customerId = params.get("customer_id");
        const entityId = params.get("entity_id");
        const formId = params.get("form_id");
        const mode = params.get("prefill");

        setPrefillParams({
            customerId: customerId ? parseInt(customerId, 10) : null,
            entityId: entityId ? parseInt(entityId, 10) : null,
            formId: formId ? parseInt(formId, 10) : null,
            mode: mode || null,
        });
    }, [formEntryId, location.search]);

    useEffect(() => {
        if (formEntryId != null || !prefillParams) {
            return;
        }

        if (!prefillCustomerApplied && prefillParams.customerId) {
            const customer = customers.find((c) => c.id === prefillParams.customerId) || null;
            dispatch({
                type: EDITOR_FORM_ENTRY_CUSTOMER_CHANGED,
                payload: {
                    customer_id: prefillParams.customerId,
                    customer,
                },
            });
            if (customer) {
                setPrefillCustomerApplied(true);
            }
        }

        if (!prefillEntityApplied && prefillParams.entityId) {
            const entity = entities.find((e) => e.id === prefillParams.entityId) || null;
            dispatch({
                type: EDITOR_FORM_ENTRY_ENTITY_CHANGED,
                payload: {
                    entity_id: prefillParams.entityId,
                    entity,
                },
            });
            if (entity) {
                setPrefillEntityApplied(true);
            }
        }

        if (!prefillFormApplied && prefillParams.formId) {
            dispatch(getFormForNewFormEntry(prefillParams.formId));
            setPrefillFormApplied(true);
        }
    }, [
        formEntryId,
        prefillParams,
        prefillCustomerApplied,
        prefillEntityApplied,
        prefillFormApplied,
        customers,
        entities,
    ]);

    useEffect(() => {
        if (formEntryId != null || !prefillParams || prefillParams.mode !== "generated_offer") {
            return;
        }

        if (offerPrefillApplied) {
            return;
        }

        if (!formEntry?.form_id || !formEntry?.form_revision?.form_data) {
            return
        }

        const raw = sessionStorage.getItem("generated_offer_data");

        if (!raw) {
            setOfferPrefillApplied(true);
            return;
        }

        try {
            const offerData = JSON.parse(raw);
            const nextFormData = buildOfferFormData(offerData, formEntry);
            if (!nextFormData) {
                setOfferPrefillApplied(true);
                return;
            }

            onUpdateFormData(nextFormData);
            setOfferPrefillVersion((prev) => prev + 1);

        } catch (e) {
            console.error(e);
            // ignore malformed stored data
            setOfferPrefillApplied(true)
        } finally {
            setOfferPrefillApplied(true)
        }

    }, [
        formEntryId,
        prefillParams,
        offerPrefillApplied,
        formEntry?.form_id,
        formEntry?.form_revision?.form_data,
    ]);

    useEffect(() => {
        setBuyerOrderReference(formEntry?.buyer_order_reference_number || "");
    }, [formEntry?.buyer_order_reference_number]);

    useEffect(() => {
        setZugferdBtFields(formEntry?.zugferd_bt_fields || {});
    }, [formEntry?.zugferd_bt_fields]);

    const isNew = () => {
        return formEntry.id === undefined;
    };

    const changeName = (name) => {
        formEntry.name = name;
    };

    const discardFormEntry = () => {
        dispatch({ type: EDITOR_FORM_ENTRY });
        dispatch(push("/entries"));
    };

    const onUpdateCustomer = (option) => {
        setMetaChanged(true);
        const customer = customers.find((c) => c.id === option.value);
        dispatch({
            type: EDITOR_FORM_ENTRY_CUSTOMER_CHANGED,
            payload: {
                customer_id: option.value,
                customer,
            },
        });

        if (isNew()) {
            // really, why do i need this?
            dispatch({
                type: EDITOR_FORM_ENTRY_FORM_CHANGED,
                payload: null,
            });
        }

        dispatch({
            type: EDITOR_FORM_ENTRY_ENTITY_CHANGED,
            payload: {
                entity_id: null,
                entity: null,
            },
        });
    };

    const onUpdateEntity = (option) => {
        setMetaChanged(true);
        const entity = entities.find((e) => e.id === option.value);
        dispatch({
            type: EDITOR_FORM_ENTRY_ENTITY_CHANGED,
            payload: {
                entity_id: option.value,
                entity,
            },
        });
    };

    const onUpdateForm = (option) => {
        if (option.value) {
            dispatch(getFormForNewFormEntry(option.value));
        }
    };

    const onUpdateFormData = (formData) => {
        const name = formEntryName(formEntry)
        dispatch({
            type: EDITOR_FORM_ENTRY_FORM_DATA_CHANGED,
            payload: {
                form_data: formData,
                name: name,
            },
        });
    };


    const onUpdateUnlockMeta = (metaEnabled) => {
        setMetaUnlocked(metaEnabled);
    };

    const handleEmptyRequiredFields = (requiredFields) => {
        requiredFields.forEach((emptyField) => {
            emptyField.classList.add("myEmpty");
        });
        alert("Der Eintrag ist noch nicht komplett ausgefüllt.");
        return requiredFields[0].scrollIntoView();
    };

    const submitFormEntry = async (mode = "new") => {
        const formRef = document.querySelector("form");
        let requiredFields = Array.from(formRef.querySelectorAll("[required]")).filter((e) =>
            isVisible(e)
        ) as Element[];

        if (!validateRequiredFields(requiredFields, handleEmptyRequiredFields)) {
            return;
        }

        if (isInvoiceForm()) {
            setPendingSubmissionMode(mode);
            setShowBuyerOrderModal(true);
            return;
        }

        await performFormSubmission(mode);
    };

    const performFormSubmission = async (
        mode = "new",
        buyerOrderRef = null,
        btFields: any = null
    ) => {
        const hasBuyerOrderRefOverride =
            buyerOrderRef !== null && buyerOrderRef !== undefined;
        const resolvedBuyerOrderReference = hasBuyerOrderRefOverride
            ? buyerOrderRef
            : buyerOrderReference || null;
        const toastId = displayLoadingToast(mode);
        if (mode === "new" || isNew()) {
            const finalBuyerOrderRef = resolvedBuyerOrderReference;
            const finalBtFields = btFields || zugferdBtFields || {};
            const formEntryWithBuyerRef = {
                ...formEntry,
                buyer_order_reference_number: finalBuyerOrderRef,
                zugferd_bt_fields: finalBtFields,
            };

            await dispatch(
                createFormEntry(
                    formEntryWithBuyerRef,
                    (formEntryData) => {
                        dispatch(push("/entries"));
                        if (formEntryData) {
                            displaySuccessToast(toastId, mode);
                        } else {
                            displayErrorToast(toastId);
                        }
                    },
                    () => {
                        displayErrorToast(toastId);
                    }
                )
            );
        } else {
            // return to previous page, we assume everything works and notify
            // the user later if any error occurred.
            history.back();

            await dispatch(
                updateFormEntryFrontend(
                    formEntry.id,
                    getFormData("form-entry-" + formEntry.id),
                    {
                        form_revision_id: formEntry.form_revision_id,
                        form_id: formEntry.form_id,
                        name: formEntry.name,
                        entity_id: formEntry.entity_id,
                        buyer_order_reference_number: resolvedBuyerOrderReference,
                        zugferd_bt_fields: btFields || zugferdBtFields || {},
                    },
                    mode,
                    (fromEntryData) => {
                        if (fromEntryData) {
                            displaySuccessToast(toastId, mode);
                            dispatch({
                                type: EDITOR_FORM_ENTRY,
                            });
                            dispatch(getFormEntry(formEntry.id));
                        } else {
                            displayErrorToast(toastId);
                        }
                    }
                )
            );
        }
    };

    const displayLoadingToast = (mode) => {
        const toastMessage =
            mode === "override"
                ? "Eintrag wird überschrieben..."
                : "Neuer Eintrag wird erstellt...";

        return toast(toastMessage, { autoClose: false });
    };

    const displaySuccessToast = (toastId, mode) => {
        toast.update(toastId, {
            render: `🦄 Eintrag ${mode === "override" ? "überschrieben." : "erstellt. Bitte warten..."
                }`,
            type: toast.TYPE.SUCCESS,
            position: "top-right",
            autoClose: 2000,
        });
    };

    const displayErrorToast = (toastId) => {
        toast.update(toastId, {
            render: "😟 Ein Fehler ist unterlaufen, bitte versuchen Sie es erneut..",
            type: toast.TYPE.ERROR,
            position: "top-right",
            autoClose: 2000,
        });
    };

    const removeEntry = async () => {
        if (confirm("Möchten Sie diesen Eintrag entfernen?")) {
            await dispatch(deleteFormEntry(formEntry));
            await dispatch(push("/entries"));
        }
    };

    const metaLockButtonStatus = () => {
        if (isNew()) {
            return "hidden";
        } else {
            return metaChanged ? "readable" : "editable";
        }
    };

    const formIsTemplatable = () => {
        return formEntry?.form?.templatable_form_entry_creation;
    };

    const isInvoiceForm = () => {
        return formEntry?.form?.name === "000_Rechnung" || formEntry?.form?.name?.includes("Rechnung");
    };

    const handleBuyerOrderSave = (payload: IZugferdBtPayload) => {
        setBuyerOrderReference(payload.buyer_order_reference_number || "");
        setZugferdBtFields(payload.zugferd_bt_fields || {});
        setShowBuyerOrderModal(false);

        if (pendingSubmissionMode) {
            performFormSubmission(
                pendingSubmissionMode,
                payload.buyer_order_reference_number || "",
                payload.zugferd_bt_fields || {}
            );
            setPendingSubmissionMode(null);
        }
    };

    const handleBuyerOrderClose = () => {
        setShowBuyerOrderModal(false);
        setPendingSubmissionMode(null);
    };

    return (
        <>
            {!isNew() && (
                <PageNavbar
                    rename={false}
                    onRename={changeName}
                    title={`Editor`}
                    description={
                        "Hier werden Formulare bearbeitet oder zwischen Kunden/Objekten bewegt"
                    }
                    subtitle={"Bearbeiten/Verschieben"}
                >
                    <PageNavAction
                        data-back-button
                        onClick={() => window.history.back()}
                        icon="chevron-left"
                        title="Zurück"
                    />
                    <PageNavAction
                        onClick={discardFormEntry}
                        danger={true}
                        title="Verwerfen"
                        icon="trash"
                    />

                    {!metaUnlocked && (
                        <>
                            <PageNavAction
                                onClick={() => submitFormEntry("new")}
                                success={formIsTemplatable()}
                                disabled={!formIsTemplatable()}
                                icon="save"
                                title="Speichern (neuer Eintrag)"
                                hint={
                                    formIsTemplatable()
                                        ? "Speichern"
                                        : "für diesen Formular-Typ nicht verfügbar"
                                }
                            />
                            <PageNavAction
                                onClick={() => submitFormEntry("override")}
                                danger={true}
                                icon="save"
                                title="Überschreiben"
                            />
                            <PageNavAction
                                onClick={removeEntry}
                                danger={true}
                                icon="trash"
                                title=""
                                hint="löschen"
                            />
                        </>
                    )}
                    {metaUnlocked && (
                        <>
                            <PageNavAction
                                onClick={() => window.location.reload()}
                                danger={true}
                                icon="trash"
                                title="Verwerfen"
                            />
                            <PageNavAction
                                onClick={() => submitFormEntry("override")}
                                success={true}
                                icon="save"
                                title="Verschieben"
                            />
                        </>
                    )}
                </PageNavbar>
            )}
            {isNew() && (
                <PageNavbar
                    rename={false}
                    onRename={changeName}
                    title={`Editor`}
                    description={"Hier werden Formulare neu erstellt"}
                    subtitle={"Anlegen"}
                >
                    <PageNavAction
                        data-back-button={"new"}
                        onClick={() => window.history.back()}
                        icon="chevron-left"
                        title="Zurück"
                    />
                    {!isLoading && (
                        <PageNavAction
                            onClick={discardFormEntry}
                            danger={true}
                            title="Verwerfen"
                            icon="trash"
                        />
                    )}
                    {!isLoading && (
                        <PageNavAction
                            onClick={submitFormEntry}
                            success={true}
                            icon="save"
                            title="Speichern (neuer Eintrag)"
                        />
                    )}
                </PageNavbar>
            )}
            <FormEntryFilter
                onUpdateCustomer={onUpdateCustomer}
                onUpdateEntity={onUpdateEntity}
                onUpdateForm={onUpdateForm}
                onUpdateFormEntry={doNothing}
                onUpdateCustomerAndEntityLock={onUpdateUnlockMeta}
                formIncludeAllOption={false}
                formEntry={formEntry}
                customerAndEntity={metaLockButtonStatus()}
                hideFormSelector={!isNew()}
            />
            {isLoading && <Loader />}
            {!isLoading && formEntry && (
                <FormRenderer
                    onSubmit={doNothing}
                    onChange={onUpdateFormData}
                    name={formEntry.name}
                    /** storno */
                    readonly={Boolean((formEntry as any)?.cancellation || (formEntry as any)?.invoice)}
                    formEntry={formEntry}
                    refreshKey={offerPrefillVersion}
                />
            )}

            <ModalBuyerOrderReference
                entry={formEntry}
                show={showBuyerOrderModal}
                onSave={handleBuyerOrderSave}
                onClose={handleBuyerOrderClose}
            />
        </>
    );
};

function mapStateToProps(state) {
    return { device: state.device, formEntries: state.formEntries };
}

export default withRouter(connect(mapStateToProps, actions)(NewEntryPage));
